import React, { useCallback, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { HuQrAuthRequest } from "src/types";
import { useCall } from "src/call-sdk/context/CallContext";
import { useToast } from "src/context/ToastContext";
import Messages from "src/config/messages.constants";
import { generateDeviceId, getDeviceId } from "src/lib/generateDeviceId";
import styled from "styled-components";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import { Typography } from "@mui/material";
import ErrorBoundary from "../ErrorBoundary"; // Import the ErrorBoundary component
import LocalStorage from "../../call-sdk/lib/local-storage";
import StorageContants from "src/config/storage.constants";
import Config from "../../config";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh; /* 100% of the viewport height */
  background-color: white;
`;

const LogoContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 25px;
`;

const SpinnerContainer = styled.div`
  flex: 1; /* Allow the spinner container to take all available vertical space */
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px; /* Add some bottom margin to create space at the bottom */
`;

const Text = styled(Typography)`
  font-size: 20px;
  font-weight: 800;
  margin-top: 20px;
`;

const Index = () => {
  const regionConfig = Config[`${process.env.REACT_APP_REGION}`];

  const { request_id } = useParams();
  const isAuthQrFetchedRef = useRef(false);
  const {
    getHUAuthenticationToken,
    authLoginResponse,
    init,
    refreshedAccessToken,
    apiErrorFlag,
  } = useCall();

  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    LocalStorage.set(StorageContants.IS_HU_USER, true);
  }, []);

  const startWebSocket = useCallback(
    (initialize: any) => {
      const deviceId = getDeviceId();
      init({
        userId: initialize.userId,
        deviceId,
        // On a WebSocket auth rejection, toast the server message and send the HU guest to
        // the terminal /qr-error ("please re-scan") page so the flow doesn't hang on
        // "Verifying…". forceShutdown has already stopped reconnects.
        onAuthFailure: (message) => {
          toast.error(message || Messages.SESSION_ERROR);
          //navigate({ pathname: "/qr-error" }, { replace: true });
        },
      });
    },
    [init, toast],
  );

  useEffect(() => {
    if (request_id && !isAuthQrFetchedRef.current) {
      LocalStorage.set(StorageContants.REQUEST_ID, request_id);
      localStorage.removeItem("hasReloaded");
      const deviceid = generateDeviceId();
      let authRequestBody: HuQrAuthRequest = {
        requestId: request_id,
        deviceid,
      };
      getHUAuthenticationToken(authRequestBody);
      isAuthQrFetchedRef.current = true;
    }
  }, [getHUAuthenticationToken, request_id]);

  useEffect(() => {
    if (authLoginResponse) {
      const { uuid } = authLoginResponse;

      let initialize = {
        userId: uuid,
      };
      startWebSocket(initialize);
    }
  }, [authLoginResponse, startWebSocket, navigate]);

  useEffect(() => {
    if (refreshedAccessToken) {
      navigate({ pathname: "/qr-business" }, { replace: true });
    }
  }, [refreshedAccessToken, navigate]);

  useEffect(() => {
    if (apiErrorFlag || !request_id) {
      navigate({ pathname: "/qr-error" }, { replace: true });
    }
  }, [apiErrorFlag, navigate, request_id]);

  return (
    <ErrorBoundary>
      <Container>
        <LogoContainer>
          <img
            src={regionConfig.appLogoImageSrc}
            alt="Logo"
            width="100%"
            height={regionConfig.appLogoImageHeight}
          />
        </LogoContainer>

        <SpinnerContainer>
          <Stack sx={{ color: "grey.500" }} spacing={2} direction="row">
            <CircularProgress color="success" />
          </Stack>
          <Text>Verifying...</Text>
        </SpinnerContainer>
      </Container>
    </ErrorBoundary>
  );
};

export default Index;
